import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AgEditorComponent } from 'ag-grid-angular';
import { Observable, Subject } from 'rxjs';
import { finalize, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { IhmLoadingIndicatorService } from '../../../shared/services/ihm-loading-indicator.service';

/**
 * To create autocomplete with object on optionsList, you MUST specific which is the filteredProperty
 * Eg.: {
 *   component: 'autocompleteEditor',
 *    params: {
 *      value: this.salesforceService.eventsHolder.map.get(line.event),
 *      optionsList: this.salesforceService.getEventsByType(line.eventReference), ==> array object
 *      formatValue: (value: Event): string => (value ? value.name : ''),
 *      filteredProperty: 'name'
 *    }
 *  };
 *
 * To create autocomplete with primitive on optionsList, you don't specific which is the filteredProperty
 * Eg.: {
 *    component: 'autocompleteEditor',
 *    params: {
 *      value: line.spots.spotLength,
 *      optionsList: spotLengthList, => array of number
 *      formatValue: value => this.utils.getSpotLengthLabel(value)
 *    }
 *  };
 */
@Component({
  selector: 'app-autocomplete-editor',
  templateUrl: './autocomplete-editor.component.html',
  styleUrls: ['./autocomplete-editor.component.scss']
})
export class AutocompleteEditorComponent implements AgEditorComponent, AfterViewInit, OnInit, OnDestroy {
  constructor(private readonly loadingIndicatorService: IhmLoadingIndicatorService) {}
  myControl = new FormControl();
  public optionsList: any[];
  public optionsListAsync: Observable<any[]>;
  public maxlength = 50;
  public fieldValue: any;
  public filteredProperty: string;
  public width: string;
  public tooltipDelay = 1000;
  public showTooltip: boolean;

  @ViewChild('container', { read: ViewContainerRef }) public container;
  filteredOptions: Observable<string[]>;
  private params: any;

  private isSelectedOption = false;
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  public formatValue: Function = value => value;
  public formatTooltip: Function = value => value;

  ngOnInit() {}

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.unsubscribe();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.container.element.nativeElement.select();
      this.container.element.nativeElement.focus();
    });
  }

  agInit(params: any): void {
    this.params = params;
    this.isSelectedOption = false;

    this.loadOptionData();

    this.formatValue = params.formatValue || this.formatValue;
    this.formatTooltip = params.formatTooltip || this.formatValue;
    this.maxlength = params.maxlength || this.maxlength;
    this.filteredProperty = params.filteredProperty;
    this.fieldValue = this.existingValue(params.value);
    this.width = params.width || 200;
    this.showTooltip = params.showTooltip;
    this.tooltipDelay = params.tooltipDelay || this.tooltipDelay;
    this.setValueOnMyControl(this.fieldValue);
  }

  getValue(): any {
    if (this.isSelectedOption) {
      return this.fieldValue;
    }

    const matches = this.filterOptionListBasedOn(this.myControl.value);
    if (!matches) {
      return null;
    }

    return matches.length > 0 ? matches[0] : this.fieldValue;
  }

  isPopup(): boolean {
    return false;
  }

  getDisplayFn() {
    return value => this.displayFn(value);
  }

  displayFn(value) {
    return value ? this.formatValue(value) : this.formatValue(this.fieldValue);
  }

  onOptionSelected(value) {
    this.isSelectedOption = true;
    this.fieldValue = value;
    this.setValueOnMyControl(value);
    this.params.api.stopEditing();
  }

  private setValueOnMyControl(value: any): void {
    if (!value) {
      return;
    }

    if (this.filteredProperty) {
      this.myControl.setValue(value[this.filteredProperty].toString());
    } else {
      this.myControl.setValue(value.toString());
    }
  }

  private filterOptionListBasedOn(value: string): any {
    if (!value || !this.optionsList) {
      return this.optionsList;
    }

    const filterValue: string = value.toLowerCase();

    if (this.filteredProperty) {
      return this.optionsList.filter(
        option => option[this.filteredProperty] && option[this.filteredProperty].toLowerCase().includes(filterValue)
      );
    }

    return this.optionsList.filter(option => this.formatValue(option.toString().toLowerCase()).includes(filterValue));
  }

  private existingValue(value: any): any {
    if (typeof value === 'string') {
      const hasExistingValue: any[] = this.filterOptionListBasedOn(value);

      return hasExistingValue ? hasExistingValue[0] : value;
    }

    return value;
  }

  private loadOptionData(): void {
    if (this.params.optionsListAsync) {
      this.loadingIndicatorService.toggle(true);

      this.params.optionsListAsync
        .pipe(
          tap((values: any) => {
            this.optionsList = values;
            this.loadFilteredOptions();
          }),
          finalize(() => this.loadingIndicatorService.toggle(false))
        )
        .subscribe();
    } else {
      this.optionsList = this.params.optionsList;
      this.loadFilteredOptions();
    }
  }

  private loadFilteredOptions(): void {
    this.filteredOptions = this.myControl.valueChanges.pipe(
      takeUntil(this.ngUnsubscribe),
      startWith(''),
      map(value => (typeof value === 'string' ? value : value[this.filteredProperty])),
      map(name => (name ? this.filterOptionListBasedOn(name) : this.optionsList))
    );
  }
}
